import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ListSelector } from "../components/ListSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { ArrowLeft, Loader2, Server, Save, Search, X, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const CONNECTION_TYPES = ["RTB", "VAST", "Prebid", "JS"];
const RTB_ROUTING_OPTIONS = ["RTB_TO_ALL", "RTB_TO_RTB", "RTB_TO_VAST", "RTB_TO_JS"];
const AD_FORMATS = ["Banner", "Native", "Video", "Rewarded_video", "Audio"];
const TRAFFIC_TYPES = ["Mobile_web", "Mobile_app", "Desktop", "CTV"];
const MARGIN_TYPES = ["Fixed", "Adaptive", "Range"];
const IMPRESSIONS_TRIGGERING = ["ADM", "ADM + bURL"];

// IAB Category Selector Component
// Simple visual checkbox component to avoid Radix Checkbox re-render issues
function SimpleCheckbox({ checked }) {
  return (
    <div 
      className={`h-4 w-4 shrink-0 rounded-sm border border-primary shadow flex items-center justify-center ${
        checked ? 'bg-primary text-primary-foreground' : ''
      }`}
    >
      {checked && (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-3 w-3">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      )}
    </div>
  );
}

function IABCategorySelector({ categories, selectedCategories, onToggle, onBulkToggle }) {
  const [search, setSearch] = useState("");
  const [expandedParents, setExpandedParents] = useState(new Set());

  // Group categories into parents and children
  const groupedCategories = useMemo(() => {
    const parents = categories.filter((c) => !c.code.includes("-"));
    const children = categories.filter((c) => c.code.includes("-"));
    
    return parents.map((parent) => ({
      ...parent,
      children: children.filter((child) => child.code.startsWith(parent.code + "-")),
    }));
  }, [categories]);

  const filteredCategories = useMemo(() => {
    if (!search) return groupedCategories;
    const query = search.toLowerCase();
    return groupedCategories
      .map((parent) => ({
        ...parent,
        children: parent.children.filter(
          (child) =>
            child.code.toLowerCase().includes(query) ||
            child.name.toLowerCase().includes(query)
        ),
      }))
      .filter(
        (parent) =>
          parent.code.toLowerCase().includes(query) ||
          parent.name.toLowerCase().includes(query) ||
          parent.children.length > 0
      );
  }, [groupedCategories, search]);

  const toggleParentExpand = useCallback((code, e) => {
    e.stopPropagation();
    setExpandedParents(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(code)) {
        newExpanded.delete(code);
      } else {
        newExpanded.add(code);
      }
      return newExpanded;
    });
  }, []);

  const handleToggle = useCallback((code, e) => {
    if (e) e.stopPropagation();
    
    // Check if this is a parent category (no dash in code)
    const isParent = !code.includes("-");
    
    if (isParent && onBulkToggle) {
      // Find the parent and its children
      const parent = groupedCategories.find(p => p.code === code);
      if (parent && parent.children.length > 0) {
        const allCodes = [parent.code, ...parent.children.map(c => c.code)];
        const parentSelected = selectedCategories.includes(code);
        
        if (parentSelected) {
          // Deselect parent and all children
          const newSelection = selectedCategories.filter(c => !allCodes.includes(c));
          onBulkToggle(newSelection);
        } else {
          // Select parent and all children
          const newSelection = [...new Set([...selectedCategories, ...allCodes])];
          onBulkToggle(newSelection);
        }
        return;
      }
    }
    
    // For children or when no bulk toggle available
    onToggle(code);
  }, [onToggle, onBulkToggle, selectedCategories, groupedCategories]);

  const toggleParentWithChildren = useCallback((parent, e) => {
    e.stopPropagation();
    const allCodes = [parent.code, ...parent.children.map((c) => c.code)];
    const allSelected = allCodes.every((code) => selectedCategories.includes(code));
    
    // Use bulk toggle if available
    if (onBulkToggle) {
      if (allSelected) {
        // Remove all codes
        const newSelection = selectedCategories.filter(code => !allCodes.includes(code));
        onBulkToggle(newSelection);
      } else {
        // Add all codes
        const newSelection = [...new Set([...selectedCategories, ...allCodes])];
        onBulkToggle(newSelection);
      }
    } else {
      // Fallback to single toggle
      onToggle(parent.code);
    }
  }, [selectedCategories, onToggle, onBulkToggle]);

  const handleRemoveCategory = useCallback((code, e) => {
    e.stopPropagation();
    onToggle(code);
  }, [onToggle]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search IAB categories by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Selected Categories */}
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-muted/30 max-h-24 overflow-y-auto">
          {selectedCategories.map((code) => {
            const cat = categories.find((c) => c.code === code);
            return (
              <Badge key={code} variant="secondary" className="gap-1 text-xs">
                {code} - {cat?.name || "Unknown"}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={(e) => handleRemoveCategory(code, e)}
                />
              </Badge>
            );
          })}
        </div>
      )}

      <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
        {filteredCategories.map((parent) => (
          <div key={parent.code}>
            <div
              className={`flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer ${
                selectedCategories.includes(parent.code) ? "bg-accent/10" : ""
              }`}
              onClick={(e) => handleToggle(parent.code, e)}
            >
              {parent.children.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => toggleParentExpand(parent.code, e)}
                  className="p-1 hover:bg-muted rounded"
                >
                  {expandedParents.has(parent.code) ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
              <div className="flex items-center gap-2 flex-1">
                <SimpleCheckbox checked={selectedCategories.includes(parent.code)} />
                <span className="font-medium text-sm">{parent.code}</span>
                <span className="text-sm text-muted-foreground">- {parent.name}</span>
              </div>
              {parent.children.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => toggleParentWithChildren(parent, e)}
                  className="text-xs text-muted-foreground hover:text-foreground px-2"
                >
                  Select all
                </button>
              )}
            </div>
            {expandedParents.has(parent.code) && parent.children.length > 0 && (
              <div className="ml-8 border-l">
                {parent.children.map((child) => (
                  <div
                    key={child.code}
                    className={`flex items-center gap-2 p-2 pl-4 hover:bg-muted/30 cursor-pointer ${
                      selectedCategories.includes(child.code) ? "bg-accent/5" : ""
                    }`}
                    onClick={(e) => handleToggle(child.code, e)}
                  >
                    <SimpleCheckbox checked={selectedCategories.includes(child.code)} />
                    <span className="text-sm font-mono">{child.code}</span>
                    <span className="text-sm text-muted-foreground">- {child.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {filteredCategories.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-4">
            No categories found matching "{search}"
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {selectedCategories.length} categories selected · {categories.length} total
      </p>
    </div>
  );
}

export default function SSPFormPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [dsps, setDsps] = useState([]);
  const [regions, setRegions] = useState([]);
  const [iabCategories, setIabCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    connection_type: "RTB",
    secure_endpoint: true,
    rtb_routing: "RTB_TO_ALL",
    ad_formats: ["Banner"],
    traffic_types: ["Mobile_web"],
    company_id: "",
    region: "US_EAST",
    default_max: 100,
    default_bidfloor: 0.5,
    margin_type: "Fixed",
    margin: 15,
    spend_limit: 1000,
    qps_limit: 100,
    impressions_triggering: "ADM",
    stats_api_link: "",
    filters: {
      bcat_filter: [],
      badv_filter: [],
      filter_dsp_without_url: false,
      responses_with_domain: false,
      secure_links_only: false,
      original_device_object: false,
      default_coppa: false,
      test: false,
      prioritize_geo_from_ip: false,
      allowed_dsps: [],
      filter_lists: [],
    },
    settings: {
      send_gzip_responses: false,
      send_test_responses: false,
      gdp_support: false,
      maximised_bid_price: false,
      writing_publisher_ids: false,
      automated: false,
      iab_categories: true,
      flexible_size_matching: false,
    },
  });

  // Raw string state for numeric inputs - prevents React from eating decimals/zeros
  const [rawInputs, setRawInputs] = useState({
    default_max: "100",
    default_bidfloor: "0.5",
    margin: "15",
    spend_limit: "1000",
    qps_limit: "100",
  });

  const updateRawInput = (field, value) => {
    setRawInputs((prev) => ({ ...prev, [field]: value }));
  };

  const commitRawInput = (field, isInteger) => {
    const raw = rawInputs[field];
    if (raw === "" || raw === undefined) {
      updateField(field, isInteger ? 0 : 0);
      return;
    }
    const parsed = isInteger ? parseInt(raw, 10) : parseFloat(raw);
    if (!isNaN(parsed)) {
      updateField(field, parsed);
    }
  };

  useEffect(() => {
    fetchReferenceData();
    if (isEdit) {
      fetchSSP();
    }
  }, [id]);

  const fetchReferenceData = async () => {
    try {
      const [companiesRes, dspsRes, regionsRes, iabRes] = await Promise.all([
        api.get("/companies"),
        api.get("/dsps"),
        api.get("/reference/regions"),
        api.get("/reference/iab-categories"),
      ]);
      setCompanies(companiesRes.data);
      setDsps(dspsRes.data);
      setRegions(regionsRes.data);
      setIabCategories(iabRes.data);
    } catch (error) {
      toast.error("Failed to load reference data");
    }
  };

  const fetchSSP = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/ssps/${id}`);
      const data = response.data;
      setFormData(data);
      // Sync raw inputs from loaded data
      setRawInputs({
        default_max: String(data.default_max ?? "100"),
        default_bidfloor: String(data.default_bidfloor ?? "0.5"),
        margin: String(data.margin ?? "15"),
        spend_limit: String(data.spend_limit ?? "1000"),
        qps_limit: String(data.qps_limit ?? "100"),
      });
    } catch (error) {
      toast.error("Failed to fetch SSP");
      navigate("/ssps");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Commit raw inputs to formData before validation
    const submitData = { ...formData };
    submitData.default_max = rawInputs.default_max === "" ? 0 : parseInt(rawInputs.default_max, 10) || 0;
    submitData.default_bidfloor = rawInputs.default_bidfloor === "" ? 0 : parseFloat(rawInputs.default_bidfloor) || 0;
    submitData.margin = rawInputs.margin === "" ? 0 : parseFloat(rawInputs.margin) || 0;
    submitData.spend_limit = rawInputs.spend_limit === "" ? 0 : parseFloat(rawInputs.spend_limit) || 0;
    submitData.qps_limit = rawInputs.qps_limit === "" ? 0 : parseInt(rawInputs.qps_limit, 10) || 0;

    // Validate mandatory fields
    if (!submitData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!submitData.company_id) {
      toast.error("Company is required");
      return;
    }
    if (!submitData.default_max || submitData.default_max <= 0) {
      toast.error("Default Tmax is required and must be greater than 0");
      return;
    }
    if (submitData.default_bidfloor === undefined || submitData.default_bidfloor === null || submitData.default_bidfloor < 0) {
      toast.error("Default Bidfloor is required");
      return;
    }
    if (!submitData.margin_type) {
      toast.error("Margin Type is required");
      return;
    }
    if (submitData.margin === undefined || submitData.margin === null || submitData.margin < 0) {
      toast.error("Margin is required");
      return;
    }
    if (!submitData.spend_limit || submitData.spend_limit <= 0) {
      toast.error("Spend Limit is required and must be greater than 0");
      return;
    }
    if (!submitData.qps_limit || submitData.qps_limit <= 0) {
      toast.error("QPS Limit is required and must be greater than 0");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/ssps/${id}`, submitData);
        toast.success("SSP updated successfully");
      } else {
        await api.post("/ssps", submitData);
        toast.success("SSP created successfully");
      }
      navigate("/ssps");
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string errors
      const detail = error.response?.data?.detail;
      let errorMsg = "Failed to save SSP";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateFilter = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      filters: { ...prev.filters, [field]: value },
    }));
  };

  const updateSetting = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [field]: value },
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => {
      const arr = prev[field];
      const newArr = arr.includes(item)
        ? arr.filter((i) => i !== item)
        : [...arr, item];
      return { ...prev, [field]: newArr };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ssp-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/ssps")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Server className="h-8 w-8 text-accent" />
            {isEdit ? "Edit SSP" : "Create SSP"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Update SSP endpoint configuration" : "Configure a new Supply-Side Platform endpoint"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="traffic">Traffic</TabsTrigger>
            <TabsTrigger value="filters">Filters</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Configure the basic SSP endpoint settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="Enter SSP name"
                      data-testid="ssp-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Select
                      value={formData.company_id}
                      onValueChange={(v) => updateField("company_id", v)}
                    >
                      <SelectTrigger data-testid="company-select">
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="connection_type">Connection Type</Label>
                    <Select
                      value={formData.connection_type}
                      onValueChange={(v) => updateField("connection_type", v)}
                    >
                      <SelectTrigger data-testid="connection-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONNECTION_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="region">Region *</Label>
                    <Select
                      value={formData.region}
                      onValueChange={(v) => updateField("region", v)}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rtb_routing">RTB Routing</Label>
                    <Select
                      value={formData.rtb_routing}
                      onValueChange={(v) => updateField("rtb_routing", v)}
                    >
                      <SelectTrigger data-testid="rtb-routing-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RTB_ROUTING_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label>Secure Endpoint</Label>
                      <p className="text-sm text-muted-foreground">
                        Require HTTPS connection
                      </p>
                    </div>
                    <Switch
                      checked={formData.secure_endpoint}
                      onCheckedChange={(v) => updateField("secure_endpoint", v)}
                      data-testid="secure-endpoint-switch"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="default_max">Default Tmax *</Label>
                    <Input
                      id="default_max"
                      type="text"
                      inputMode="numeric"
                      value={rawInputs.default_max}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateRawInput("default_max", val);
                        }
                      }}
                      onBlur={() => commitRawInput("default_max", true)}
                      placeholder="100"
                      data-testid="default-max-input"
                    />
                    <p className="text-xs text-muted-foreground">Maximum response time in ms</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default_bidfloor">Default Bidfloor *</Label>
                    <Input
                      id="default_bidfloor"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.default_bidfloor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("default_bidfloor", val);
                        }
                      }}
                      onBlur={() => commitRawInput("default_bidfloor", false)}
                      placeholder="0.50"
                      data-testid="bidfloor-input"
                    />
                    <p className="text-xs text-muted-foreground">Minimum bid price in USD</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="margin_type">Margin Type *</Label>
                    <Select
                      value={formData.margin_type}
                      onValueChange={(v) => updateField("margin_type", v)}
                    >
                      <SelectTrigger data-testid="margin-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARGIN_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="margin">Margin (%) *</Label>
                    <Input
                      id="margin"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.margin}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("margin", val);
                        }
                      }}
                      onBlur={() => commitRawInput("margin", false)}
                      placeholder="15"
                      data-testid="margin-input"
                    />
                    <p className="text-xs text-muted-foreground">Revenue margin percentage</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="spend_limit">Spend Limit *</Label>
                    <Input
                      id="spend_limit"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.spend_limit}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("spend_limit", val);
                        }
                      }}
                      onBlur={() => commitRawInput("spend_limit", false)}
                      placeholder="1000"
                      data-testid="spend-limit-input"
                    />
                    <p className="text-xs text-muted-foreground">Daily spend limit in USD</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="qps_limit">QPS Limit *</Label>
                    <Input
                      id="qps_limit"
                      type="text"
                      inputMode="numeric"
                      value={rawInputs.qps_limit}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateRawInput("qps_limit", val);
                        }
                      }}
                      onBlur={() => commitRawInput("qps_limit", true)}
                      placeholder="100"
                      data-testid="qps-limit-input"
                    />
                    <p className="text-xs text-muted-foreground">Maximum queries per second</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Endpoint & API</CardTitle>
                <CardDescription>Configure endpoint and statistics settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="impressions_triggering">Impressions Triggering</Label>
                    <Select
                      value={formData.impressions_triggering}
                      onValueChange={(v) => updateField("impressions_triggering", v)}
                    >
                      <SelectTrigger data-testid="impressions-triggering-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {IMPRESSIONS_TRIGGERING.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                <div className="space-y-2">
                  <Label htmlFor="stats_api_link">Statistics API Link</Label>
                  <Input
                    id="stats_api_link"
                    value={formData.stats_api_link}
                    onChange={(e) => updateField("stats_api_link", e.target.value)}
                    placeholder="https://api.example.com/stats?date=[%Y-m-d%]"
                    data-testid="stats-api-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use [%Y-m-d%] as a placeholder for the date
                  </p>
                </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Traffic Tab */}
          <TabsContent value="traffic" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ad Formats</CardTitle>
                <CardDescription>Select supported ad formats</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.ad_formats.length === AD_FORMATS.length) {
                        updateField("ad_formats", []);
                      } else {
                        updateField("ad_formats", [...AD_FORMATS]);
                      }
                    }}
                    data-testid="select-all-ad-formats"
                  >
                    {formData.ad_formats.length === AD_FORMATS.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {AD_FORMATS.map((format) => (
                    <div key={format} className="flex items-center space-x-2">
                      <Checkbox
                        id={`format-${format}`}
                        checked={formData.ad_formats.includes(format)}
                        onCheckedChange={() => toggleArrayItem("ad_formats", format)}
                        data-testid={`ad-format-${format}`}
                      />
                      <Label htmlFor={`format-${format}`} className="cursor-pointer">
                        {format.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Traffic Types</CardTitle>
                <CardDescription>Select target traffic sources</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.traffic_types.length === TRAFFIC_TYPES.length) {
                        updateField("traffic_types", []);
                      } else {
                        updateField("traffic_types", [...TRAFFIC_TYPES]);
                      }
                    }}
                    data-testid="select-all-traffic-types"
                  >
                    {formData.traffic_types.length === TRAFFIC_TYPES.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {TRAFFIC_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`traffic-${type}`}
                        checked={formData.traffic_types.includes(type)}
                        onCheckedChange={() => toggleArrayItem("traffic_types", type)}
                        data-testid={`traffic-type-${type}`}
                      />
                      <Label htmlFor={`traffic-${type}`} className="cursor-pointer">
                        {type.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Filters Tab */}
          <TabsContent value="filters" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Category & Advertiser Filters</CardTitle>
                <CardDescription>Block specific IAB categories or advertisers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Blocked Categories (BCAT)</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Select IAB categories to block from this SSP endpoint
                  </p>
                  <IABCategorySelector
                    categories={iabCategories}
                    selectedCategories={formData.filters.bcat_filter}
                    onToggle={(code) => {
                      const current = formData.filters.bcat_filter;
                      const newFilter = current.includes(code)
                        ? current.filter((c) => c !== code)
                        : [...current, code];
                      updateFilter("bcat_filter", newFilter);
                    }}
                    onBulkToggle={(newSelection) => {
                      updateFilter("bcat_filter", newSelection);
                    }}
                  />
                </div>

                <div className="space-y-3">
                  <Label>Blocked Advertisers (BADV)</Label>
                  <p className="text-sm text-muted-foreground">
                    Enter advertiser domains separated by commas
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="example.com, blocked.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          const value = e.target.value.trim().replace(/,+$/, '');
                          if (value) {
                            const domains = value.split(',').map(s => s.trim()).filter(Boolean);
                            const current = formData.filters.badv_filter || [];
                            const newDomains = domains.filter(d => !current.includes(d));
                            if (newDomains.length > 0) {
                              updateFilter("badv_filter", [...current, ...newDomains]);
                            }
                            e.target.value = '';
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim().replace(/,+$/, '');
                        if (value) {
                          const domains = value.split(',').map(s => s.trim()).filter(Boolean);
                          const current = formData.filters.badv_filter || [];
                          const newDomains = domains.filter(d => !current.includes(d));
                          if (newDomains.length > 0) {
                            updateFilter("badv_filter", [...current, ...newDomains]);
                          }
                          e.target.value = '';
                        }
                      }}
                      data-testid="badv-input"
                    />
                  </div>
                  {formData.filters.badv_filter && formData.filters.badv_filter.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30 max-h-32 overflow-y-auto">
                      {formData.filters.badv_filter.map((domain) => (
                        <Badge key={domain} variant="secondary" className="gap-1 text-sm py-1 px-2">
                          {domain}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => {
                              const newFilter = formData.filters.badv_filter.filter(d => d !== domain);
                              updateFilter("badv_filter", newFilter);
                            }}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formData.filters.badv_filter?.length || 0} domain(s) blocked
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Response Filters</CardTitle>
                <CardDescription>Configure response filtering options</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "filter_dsp_without_url", label: "Filter out DSP responses without URL" },
                    { key: "responses_with_domain", label: "Responses with domain" },
                    { key: "secure_links_only", label: "Secure links only" },
                    { key: "original_device_object", label: "Original device object" },
                    { key: "default_coppa", label: "Default COPPA" },
                    { key: "test", label: "Test mode" },
                    { key: "prioritize_geo_from_ip", label: "Prioritize GEO from IP" },
                  ].map((filter) => (
                    <div
                      key={filter.key}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <Label htmlFor={filter.key}>{filter.label}</Label>
                      <Switch
                        id={filter.key}
                        checked={formData.filters[filter.key]}
                        onCheckedChange={(v) => updateFilter(filter.key, v)}
                        data-testid={`filter-${filter.key}`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>DSP Selection</CardTitle>
                <CardDescription>Select allowed DSP endpoints</CardDescription>
              </CardHeader>
              <CardContent>
                {dsps.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No DSPs available.</p>
                ) : (
                  <div className="space-y-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between" data-testid="dsp-select-dropdown">
                          <span className="truncate">
                            {formData.filters.allowed_dsps.length === 0
                              ? "All DSPs (no filter)"
                              : `${formData.filters.allowed_dsps.length} DSP(s) selected`}
                          </span>
                          <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[350px] p-0" align="start">
                        <div className="p-3 border-b flex items-center justify-between">
                          <span className="text-sm font-medium">Select DSPs</span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => updateFilter("allowed_dsps", dsps.map(d => d.id))}>All</Button>
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => updateFilter("allowed_dsps", [])}>None</Button>
                          </div>
                        </div>
                        <div className="max-h-[250px] overflow-y-auto p-2">
                          {dsps.map((dsp) => (
                            <label key={dsp.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer">
                              <Checkbox
                                checked={formData.filters.allowed_dsps.includes(dsp.id)}
                                onCheckedChange={() => {
                                  const arr = formData.filters.allowed_dsps;
                                  const newArr = arr.includes(dsp.id) ? arr.filter(i => i !== dsp.id) : [...arr, dsp.id];
                                  updateFilter("allowed_dsps", newArr);
                                }}
                                data-testid={`allowed-dsp-${dsp.id}`}
                              />
                              <span className="text-sm">{dsp.name}</span>
                              <Badge variant="outline" className="ml-auto text-[10px]">{dsp.status}</Badge>
                            </label>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {formData.filters.allowed_dsps.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {formData.filters.allowed_dsps.map(id => {
                          const dsp = dsps.find(d => d.id === id);
                          return dsp ? (
                            <Badge key={id} variant="secondary" className="text-xs gap-1">
                              {dsp.name}
                              <button onClick={() => updateFilter("allowed_dsps", formData.filters.allowed_dsps.filter(i => i !== id))} className="ml-1 hover:text-destructive">&times;</button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applied Filter Lists */}
            <Card>
              <CardHeader>
                <CardTitle>Filter Lists</CardTitle>
                <CardDescription>Apply allow/block lists to filter bid requests</CardDescription>
              </CardHeader>
              <CardContent>
                <ListSelector
                  api={api}
                  selected={formData.filters.filter_lists || []}
                  onChange={(lists) => updateFilter("filter_lists", lists)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Additional Settings</CardTitle>
                <CardDescription>Configure advanced SSP options</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "send_gzip_responses", label: "Send gzip responses" },
                    { key: "send_test_responses", label: "Send test responses" },
                    { key: "gdp_support", label: "GDP support" },
                    { key: "maximised_bid_price", label: "Maximised bid price" },
                    { key: "writing_publisher_ids", label: "Writing publisher IDs" },
                    { key: "automated", label: "Automated" },
                    { key: "iab_categories", label: "IAB Categories" },
                    { key: "flexible_size_matching", label: "Flexible size matching" },
                  ].map((setting) => (
                    <div
                      key={setting.key}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <Label htmlFor={setting.key}>{setting.label}</Label>
                      <Switch
                        id={setting.key}
                        checked={formData.settings[setting.key]}
                        onCheckedChange={(v) => updateSetting(setting.key, v)}
                        data-testid={`setting-${setting.key}`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
          <Button type="button" variant="outline" onClick={() => navigate("/ssps")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-ssp-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? "Update SSP" : "Create SSP"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
